import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ProvidersService, ScheduleTypeEnum } from '../../../../../../js/api';

const SCHEDULE_QUERY_KEY = 'provider-schedule';

/**
 * PATCH /api/providers/schedule/type/ — updates the caller's
 * `Provider.schedule_type`.
 *
 * `onMutate` accepts an optional `flushPending` callback so the calling
 * component can drain its debounced add/remove queue before the type flip
 * lands — otherwise the in-flight bulk write would target the old mode's
 * is_blocked bucket. After the PATCH succeeds, every cached schedule
 * window is invalidated so the next render picks up the new shape.
 */
export const useScheduleTypeMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      scheduleType,
      flushPending,
    }: {
      scheduleType: ScheduleTypeEnum;
      flushPending?: () => void | Promise<void>;
    }) => {
      if (flushPending) {
        await flushPending();
      }
      return ProvidersService.providersScheduleTypePartialUpdate({
        requestBody: { schedule_type: scheduleType },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SCHEDULE_QUERY_KEY] });
    },
  });
};
